import React from 'react';
import { ArrowLeft, ArrowRight, Clock, Users, CheckCircle2, Quote, Building2, Target, TrendingUp } from 'lucide-react';
import { caseStudiesData } from '../mock/data';
import { Button } from './ui/button';
import { openBookDemo } from '../utils/config';

const CaseStudyDetail = ({ studyId, onBack, onBackToHome }) => {
  const study = caseStudiesData.studies.find(s => s.id === studyId);

  if (!study) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Case study not found</h2>
          <Button onClick={onBack}>Back to Case Studies</Button>
        </div>
      </div>
    );
  }

  const otherStudies = caseStudiesData.studies.filter(s => s.id !== studyId).slice(0, 2);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <button 
            onClick={onBack}
            className="inline-flex items-center text-gray-600 hover:text-blue-600 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Case Studies
          </button>
        </div>
      </div>

      {/* Hero */}
      <div className="relative h-[400px] overflow-hidden">
        <img 
          src={study.heroImage}
          alt={study.company}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="container mx-auto">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center font-bold text-2xl text-gray-900">
                {study.logo}
              </div>
              <div>
                <span className="px-3 py-1 bg-blue-600 text-white text-sm font-semibold rounded-full">
                  {study.industry}
                </span>
              </div>
            </div>
            <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">
              {study.company}
            </h1>
            <div className="flex flex-wrap gap-4 text-gray-300">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" /> {study.timeline}
              </span>
              <span className="flex items-center gap-1">
                <Users className="w-4 h-4" /> {study.participants}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Results Banner */}
      <div className="bg-blue-600 py-8">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {study.results.map((result, idx) => (
              <div key={result.metric || `detail-result-${idx}`} className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-white mb-1">{result.metric}</div>
                <div className="text-blue-100 text-sm">{result.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Programs Used */}
          <div className="flex flex-wrap gap-2 mb-8">
            {study.programs.map((program, idx) => (
              <span key={program} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                {program}
              </span>
            ))}
          </div>

          {/* Challenge Section */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-lg">
                <Target className="w-6 h-6 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">The Challenge</h2>
            </div>
            <p className="text-lg text-gray-600 leading-relaxed">
              {study.challenge}
            </p>
          </section>

          {/* Solution Section */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <CheckCircle2 className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Our Solution</h2>
            </div>
            <p className="text-lg text-gray-600 leading-relaxed">
              {study.solution}
            </p>
          </section>

          {/* Results Section */}
          <section className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-green-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">The Results</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {study.results.map((result, idx) => (
                <div key={`timeline-${item.phase || idx}`} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <div className="text-4xl font-bold text-blue-600 mb-2">{result.metric}</div>
                  <div className="text-gray-700 font-medium">{result.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Testimonial */}
          <section className="mb-12">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 text-white">
              <Quote className="w-12 h-12 text-blue-400 mb-4" />
              <blockquote className="text-xl lg:text-2xl font-medium mb-6 leading-relaxed">
                "{study.testimonial.quote}"
              </blockquote>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-blue-600 rounded-full flex items-center justify-center font-bold text-lg">
                  {study.testimonial.avatar}
                </div>
                <div>
                  <div className="font-semibold text-lg">{study.testimonial.author}</div>
                  <div className="text-gray-400">{study.testimonial.role}</div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <div className="text-center p-8 bg-blue-50 rounded-2xl border border-blue-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              Ready to achieve similar results?
            </h3>
            <p className="text-gray-600 mb-6">
              Let's discuss how CoachMeUpp can transform your organization's leadership development.
            </p>
            <Button 
              onClick={openBookDemo}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8"
            >
              Book a Demo
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>

      {/* Other Case Studies */}
      {otherStudies.length > 0 && (
        <div className="bg-gray-50 py-16">
          <div className="container mx-auto px-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">More Success Stories</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {otherStudies.map((otherStudy) => (
                <div 
                  key={otherStudy.id}
                  onClick={() => window.scrollTo({ top: 0 })}
                  className="bg-white rounded-xl shadow-md overflow-hidden cursor-pointer hover:shadow-xl transition-all duration-300"
                >
                  <div className="relative h-48">
                    <img 
                      src={otherStudy.heroImage}
                      alt={otherStudy.company}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-4 left-4 flex items-center gap-3">
                      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center font-bold text-gray-900">
                        {otherStudy.logo}
                      </div>
                      <span className="text-white font-semibold">{otherStudy.company}</span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                        {otherStudy.industry}
                      </span>
                      <span className="text-sm text-gray-500">{otherStudy.timeline}</span>
                    </div>
                    <p className="text-gray-600 line-clamp-2">{otherStudy.challenge}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseStudyDetail;
